<template>
  <a-modal
    :visible="visible"
    width="1400px"
    title="添加预病历"
    :maskClosable="false"
    :keyboard="false"
    @cancel="handleCancel"
    class="pre-medical-modal-wrapper"
  >
    <div class="pre-medical-modal">
      <div class="loading" v-if="loading">
        <a-spin />
      </div>
      <template v-else>
        <!-- 头部信息 -->
        <header class="header">
          <div class="top">
            <div class="left">
              <div class="text">
                <span class="label">患者：</span>
                <PatientNameAgeReveal :name="info.patientName" :age="info.patientAge" />
              </div>
              <div class="text">
                <span v-if="info.genderStr">{{ info.genderStr }}</span>
                <span v-if="info.genderStr && info.patientAge">·</span>
                <span v-if="info.patientAge">{{ info.patientAge }}岁</span>
                <span class="relation">{{ patientRelationMap[info.relation] || '' }}</span>
              </div>
              <span class="link" @click="handleEditInfo">修改</span>
              <div class="text">
                <span class="label">医生：</span>
                {{ info.doctorName }}
              </div>
            </div>
            <div class="right">
              <a-button size="mini" @click="openHistory">预病历记录</a-button>
            </div>
          </div>
          <div class="bottom">
            <div class="cert-tip" v-if="info.recordStatusFlag === false">
              <ExclamationCircleFilled />
              医生未认证备案，暂无开方权限！请引导医生进入【我的-认证】页面提交资料
            </div>
          </div>
        </header>
        <!-- 主体部分 -->
        <div class="body">
          <!-- 左侧 -->
          <div class="panel panel—small">
            <PreSummary ref="preSummaryRef"></PreSummary>
          </div>
          <!-- 右侧 -->
          <div class="panel panel—big">
            <PrescriptionModule ref="prescriptionModuleRef" />
          </div>
        </div>
      </template>
    </div>
    <template #footer>
      <a-button key="back" @click="handleCancel">取消</a-button>
      <a-button
        key="back"
        type="primary"
        ghost
        :disabled="info.recordStatusFlag === false"
        @click="handleSave"
      >
        暂存
      </a-button>
      <a-button
        key="submit"
        type="primary"
        :loading="loading"
        :disabled="info.recordStatusFlag === false"
        @click="handleOk"
      >
        预览
      </a-button>
    </template>
    <InfoEdit ref="infoEditRef" />
    <PreviewDrawer :visible="previewVisible" @cancel="closePreview" @confirm="closeAllModal" />
    <InfoHistory ref="InfoHistoryRef" />
  </a-modal>
</template>

<script setup>
import { ref, defineExpose, computed, toRaw, nextTick } from 'vue'
import PrescriptionModule from './PrescriptionModule.vue'
import InfoHistory from './InfoHistory.vue'
import PreSummary from './PreSummary/index.vue'
import InfoEdit from './InfoEdit.vue'
import PreviewDrawer from './PreviewDrawer.vue'
import { useStore } from 'vuex'
import { patientInfo } from '@/api/login'
import { patientGenderMap, patientRelationMap } from './config'
import { message as Message } from 'ant-design-vue'
import { PRE_MEDICAL_RECORD_DRAFT_TTL, getPreMedicalDraftKey } from '@/utils/preMedicalDraft'
import { ExclamationCircleFilled } from '@ant-design/icons-vue'
import PatientNameAgeReveal from '@/components/PatientNameAgeReveal.vue'
const store = useStore()
const info = computed(() => store.state.preMedicalRecord)
const params = ref()
const visible = ref(false)
const loading = ref(false)
const previewVisible = ref(false)
const infoEditRef = ref()
const InfoHistoryRef = ref()
// 健管用户ID
const daUserId = computed(() => store.getters.userId)
// 检索性别
const handleEditInfo = () => {
  infoEditRef.value.open()
}
//
const openHistory = () => {
  InfoHistoryRef.value.open()
}
const handleCancel = () => {
  visible.value = false
  store.dispatch('preMedicalRecord/resetPreMedicalRecord')
}
const preSummaryRef = ref()
const prescriptionModuleRef = ref()
// 暂存 key 规则：按 医生 + 患者 + 健管 维度隔离，避免不同对象互相覆盖
const getDraftKey = ({ doctorId, patientId }) =>
  getPreMedicalDraftKey({ doctorId, patientId, daUserId: daUserId.value })

// 读取并校验暂存：
// 1) 不存在 -> 返回 null
// 2) 过期/解析失败 -> 立即清理并返回 null
// 3) 有效 -> 返回 data
const getValidDraft = ({ doctorId, patientId }) => {
  const draftKey = getDraftKey({ doctorId, patientId })
  const draftText = localStorage.getItem(draftKey)
  if (!draftText) {
    return null
  }
  try {
    const draft = JSON.parse(draftText)
    if (!draft?.expiresAt || Date.now() > draft.expiresAt) {
      localStorage.removeItem(draftKey)
      return null
    }
    return { key: draftKey, data: draft.data }
  } catch (error) {
    localStorage.removeItem(draftKey)
    return null
  }
}

const applyDraftToForm = draftData => {
  nextTick(() => {
    // 分别回填左/右两块表单，保证 UI 与 store 一致
    preSummaryRef.value?.setFormData?.(draftData?.consultationSummaryDTO || {})
    prescriptionModuleRef.value?.setFormData?.(draftData || {})
  })
}
// 暂存：取两个子表单当前值 + store 基础信息，写入 localStorage，并记录过期时间
const handleSave = async () => {
  try {
    const summaryData = preSummaryRef.value?.getFormData?.() || {}
    const prescriptionData = prescriptionModuleRef.value?.getFormData?.() || {}
    const mergedData = {
      ...toRaw(info.value),
      ...toRaw(summaryData),
      ...toRaw(prescriptionData)
    }
    const patientId = mergedData.patientId || params.value?.patientId || ''
    const doctorId = mergedData.doctorId || params.value?.doctorId || ''
    const draftKey = getDraftKey({ doctorId, patientId })
    const cacheData = {
      data: mergedData,
      expiresAt: Date.now() + PRE_MEDICAL_RECORD_DRAFT_TTL
    }
    localStorage.setItem(draftKey, JSON.stringify(cacheData))
    Message.success('暂存成功，30天内有效')
    closeAllModal()
  } catch (error) {
    console.warn('暂存失败:', error)
    Message.error('暂存失败，请重试')
  }
}
// 预览
const handleOk = async () => {
  try {
    const results = await Promise.all([
      preSummaryRef.value?.submitForm?.(),
      prescriptionModuleRef.value?.getMedicineDetails?.()
    ])
    const [summaryResult, prescriptionResult] = results
    console.log('预问诊小结校验结果:', toRaw(summaryResult))
    console.log('处方模块校验结果:', toRaw(prescriptionResult))
    store.dispatch('preMedicalRecord/updatePreMedicalRecord', {
      ...toRaw(summaryResult),
      ...toRaw(prescriptionResult),
      ucDaId: daUserId.value
    })
    console.log('预病历完整数据:', info.value)
    previewVisible.value = true
  } catch (error) {
    console.warn('表单校验未通过:', error)
  }
}

const closePreview = () => {
  previewVisible.value = false
}

// 获取患者数据
const getPatientInfo = async _params => {
  loading.value = true
  const p = {
    patientId: _params.patientId,
    doctorId: _params.doctorId
  }
  // 打开弹窗优先尝试恢复未过期暂存，命中则不再请求患者初始化接口
  const draft = getValidDraft(p)
  if (draft?.data) {
    store.commit('preMedicalRecord/UPDATE_PRE_MEDICAL_RECORD', draft.data)
    applyDraftToForm(draft.data)
    loading.value = false
    nextTick(() => {
      preSummaryRef.value?.initData(false)
    })
    return
  }

  try {
    const [patient] = await patientInfo(p)
    const payAmountYuan = patient.doctorConsultationFeeConfigVo
      ? patient.doctorConsultationFeeConfigVo.defaultConsultationFee / 100
      : null
    const genderStr = patientGenderMap[patient.patientProfileCommonVO.gender]?.label
    const data = {
      ...patient,
      // patientProfileCommonVO
      // 患者信息 默认获取就诊人的信息字段和提交接口保持一致
      patientName: patient.patientProfileCommonVO.patientProfileName,
      patientProfileId: patient.patientProfileCommonVO.patientProfileId,
      patientAge: patient.patientProfileCommonVO.age,
      patientGender: patient.patientProfileCommonVO.gender,
      relation: patient.patientProfileCommonVO.relation,
      genderStr: genderStr,
      recommendCureParamVO: {
        payAmountYuan: payAmountYuan
      },
      diagnosisVOList:
        patient.diagnosisVOList?.map(c => {
          return { ...c, diagnosisType: 1 }
        }) || [],
      chineseDiagnosisVOList:
        patient.chineseDiagnosisVOList?.map(c => {
          return { ...c, diagnosisType: 2 }
        }) || [],
      ...params.value
    }
    console.log('初始化数据：', data)
    store.commit('preMedicalRecord/UPDATE_PRE_MEDICAL_RECORD', data)
    loading.value = false
    nextTick(() => {
      preSummaryRef.value?.initData(true)
    })
  } catch (error) {
    console.log('初始化预病历异常：', error)
    console.log(`output->123  `, 123)
  }
}

const modalInit = _params => {
  console.log('打开预病例', _params)
  params.value = _params
  getPatientInfo(_params)
  visible.value = true
}
const closeAllModal = () => {
  visible.value = false
  previewVisible.value = false
  store.dispatch('preMedicalRecord/resetPreMedicalRecord')
}
defineExpose({ modalInit })
</script>
<style lang="scss">
.pre-medical-modal-wrapper .ant-modal-content .ant-modal-body {
  padding: 0;
}
</style>
<style scoped lang="scss">
.pre-medical-modal {
  gap: 16px;
  min-height: 360px;
  .header {
    .top {
      display: flex;
      background: #1989fa0f;
      padding: 14px 20px;
      align-items: center;
      justify-content: space-between;
      .left {
        display: flex;
        align-items: center;
        .text {
          margin-right: 8px;
          font-weight: 500;
          font-size: 16px;
          color: #333333;
          .relation {
            color: #999999;
            background: #7285961f;
            padding: 4px;
            font-size: 12px;
            border-radius: 4px;
            margin-left: 8px;
          }
        }
        .label {
          font-weight: 400;
          font-size: 16px;
          color: #999999;
        }
        .link {
          font-weight: 500;
          font-size: 16px;
          color: #1989fad9;
          margin-right: 160px;
          margin-left: 24px;
          cursor: pointer;
        }
      }
    }
    .bottom {
      .cert-tip {
        background: #ffecec;
        padding: 10px 16px;
        border-radius: 4px;
        color: #f45959;
        font-size: 15px;
        font-weight: 500;
      }
    }
  }
  .body {
    display: flex;
    background: #bdc7db;
  }
  .loading {
    width: 100%;
    height: 100%;
    background: #fff;
    text-align: center;
    margin-top: 30px;
  }
}

.panel {
  display: flex;
  flex-direction: column;
  background: #fff;
  overflow: auto;
  height: 620px;
  margin-right: 5px;
  margin-top: 5px;
  &:last-child {
    margin-right: 0;
  }
}
.panel—small {
  width: 512px;
  flex-shrink: 0;
}
.panel—big {
  flex-grow: 1;
  background: #ebeef4;
}
.placeholder {
  color: #999;
  font-size: 14px;
}
</style>
